import { DateTime } from "luxon";

import { parseRelativeDate } from "./utils";

/**
 * The dates a list filter will actually compare, written the way the query uses them.
 *
 * « next 7 days » is the window from today through the day 7 days ahead. As the From
 * bound of « between », that window's start is today, so the bound kept is the later
 * day — the same day « in 7 days » names. « last 7 days » as the To bound keeps the
 * day 7 days ago.
 */

type Span = { from: DateTime; to: DateTime };

const ROLLING = /^(next|last) (\d+) (day|week|month|year)s?$/;

function betweenEndpoint(expression: string, low: boolean): string {
    const match = ROLLING.exec(expression.trim().toLowerCase());
    if (!match) return expression;
    const [, direction, amount, unit] = match;
    const plural = amount === "1" ? "" : "s";
    if (low && direction === "next") return `in ${amount} ${unit}${plural}`;
    if (!low && direction === "last") return `back ${amount} ${unit}${plural}`;
    return expression;
}

function fixedSpan(value: string): Span | null {
    const text = value.trim();
    const written = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?$/);
    const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
    const parts = written ?? iso;
    if (!parts) return null;
    const [day, month, year, hour, minute] = written
        ? [parts[1], parts[2], parts[3], parts[4], parts[5]]
        : [parts[3], parts[2], parts[1], parts[4], parts[5]];
    const moment = DateTime.fromObject({
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: hour ? Number(hour) : 0,
        minute: minute ? Number(minute) : 0
    });
    if (!moment.isValid) return null;
    if (hour) return { from: moment, to: moment };
    return { from: moment.startOf("day"), to: moment.endOf("day") };
}

function spanOf(value: string): Span | null {
    const text = value.trim();
    if (!text) return null;
    const fixed = fixedSpan(text);
    if (fixed) return fixed;
    const relative = parseRelativeDate(text);
    if (!relative) return null;
    const from = DateTime.fromJSDate(relative.from);
    const to = DateTime.fromJSDate(relative.to);
    if (!from.isValid || !to.isValid) return null;
    return { from, to };
}

function formatWhen(moment: DateTime): string {
    const wholeDay = (moment.hour === 0 && moment.minute === 0) || (moment.hour === 23 && moment.minute === 59);
    return moment.toFormat(wholeDay ? "dd/MM/yyyy" : "dd/MM/yyyy HH:mm");
}

function formatSpan(span: Span): string {
    const from = formatWhen(span.from);
    const to = formatWhen(span.to);
    return from === to ? from : `${from} to ${to}`;
}

/** A typed calendar date already says when it is. A relative expression does not. */
export function needsDatePreview(values: string[]): boolean {
    return values.some(value => value.trim() !== "" && !fixedSpan(value));
}

/** The one day a From (`low`) or To bound of « between » compares, or null for a typed or unknown date. */
export function betweenBound(value: string, low: boolean): string | null {
    if (!needsDatePreview([value])) return null;
    const span = spanOf(betweenEndpoint(value, low));
    return span ? formatWhen(low ? span.from : span.to) : null;
}

/**
 * How `operator` reads `values`, or null when the value is not a date we understand.
 * Concrete dates are included: the caller hides the line when nothing relative is typed.
 */
export function dateQueryPreview(operator: string, values: string[]): string | null {
    if (operator === "between") {
        const [low = "", high = ""] = values;
        const from = spanOf(betweenEndpoint(low, true));
        const to = spanOf(betweenEndpoint(high, false));
        if (from && to) return `${formatWhen(from.from)} to ${formatWhen(to.to)}`;
        if (from) return `from ${formatWhen(from.from)}`;
        if (to) return `up to ${formatWhen(to.to)}`;
        return null;
    }

    const span = spanOf(values[0] ?? "");
    if (!span) return null;
    switch (operator) {
        case "on":
        case "equals":
            return formatSpan(span);
        case "not_on":
        case "not_equals":
        case "not_equal_to":
            return `not ${formatSpan(span)}`;
        case "before":
        case "lt":
            return `before ${formatWhen(span.from)}`;
        case "after":
            return `after ${formatWhen(span.to)}`;
        case "on_or_after":
        case "gte":
            return `from ${formatWhen(span.from)}`;
        case "on_or_before":
            return `up to ${formatWhen(span.to)}`;
        case "gt":
            return `after ${formatWhen(span.from)}`;
        case "lte":
            return `up to ${formatWhen(span.from)}`;
        default:
            return null;
    }
}
